import { useQuery } from '@tanstack/react-query';
import { api, type FieldPhase, type TimeEntry } from '../../lib/api.js';
import {
  formatTimer,
  phaseLabel,
  useActiveJob,
  useOpenEntrySeconds,
} from '../../lib/field-time.js';

/**
 * Per-phase time-on-job breakdown for the Onsite screen.
 *
 * Sums `durationSec` of all closed entries by phase, then adds the live
 * open-entry seconds onto the phase row that's currently in progress. The
 * total ticks every second along with the open entry so the tech sees the
 * meter moving while they work.
 *
 * Reads `/sales-orders/:id/time-entries` — same endpoint the office-side
 * SO detail panel uses. Refetches when the active phase changes (cache key
 * includes `activeKey`) so the breakdown stays fresh after Pause / Next /
 * Resume taps.
 */
export function TimeBreakdownPanel({ orderId }: { orderId: number }) {
  const activeQuery = useActiveJob();
  const active = activeQuery.data ?? null;
  const openSec = useOpenEntrySeconds(active);

  const entriesQuery = useQuery({
    queryKey: [
      'sales-orders',
      orderId,
      'time-entries',
      // Bust the cache when the open-entry identity changes (start / phase-
      // change / stop). Within a phase the local openSec ticker handles
      // visuals — no need to refetch every second.
      active?.openEntry.id ?? null,
    ],
    queryFn: ({ signal }) => api.field.entriesForOrder(orderId, signal),
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });

  const rows = computeBreakdown(entriesQuery.data ?? [], active, openSec);
  const total = rows.reduce((sum, r) => sum + r.seconds, 0);

  if (entriesQuery.isPending) {
    return (
      <div className="rounded-lg border bg-surface p-3" style={{ borderColor: 'var(--color-border-tertiary)' }}>
        <div className="text-xs uppercase tracking-wider text-ink-subtle mb-1.5">
          Time on job
        </div>
        <div className="h-12 animate-pulse rounded bg-surface-muted" />
      </div>
    );
  }

  if (entriesQuery.isError) {
    return (
      <div className="rounded-lg border bg-surface p-3" style={{ borderColor: 'var(--color-border-tertiary)' }}>
        <div className="text-xs uppercase tracking-wider text-ink-subtle mb-1.5">
          Time on job
        </div>
        <div className="text-[11px] text-ink-muted">Couldn't load time breakdown.</div>
      </div>
    );
  }

  if (rows.length === 0) {
    return null;
  }

  return (
    <div className="rounded-lg border bg-surface p-3" style={{ borderColor: 'var(--color-border-tertiary)' }}>
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="text-xs uppercase tracking-wider text-ink-subtle">Time on job</div>
        <div className="text-sm font-semibold tabular-nums">{formatTimer(total)}</div>
      </div>
      <ul className="space-y-1">
        {rows.map((row) => (
          <li
            key={row.phase}
            className="flex items-center justify-between gap-2 text-[12px]"
          >
            <span className="flex items-center gap-1.5 min-w-0">
              <span
                aria-hidden
                className="inline-block h-1.5 w-1.5 rounded-full shrink-0"
                style={{ backgroundColor: phaseDotColor(row.phase) }}
              />
              <span className="text-ink truncate">{phaseLabel(row.phase)}</span>
              {row.live && (
                <span className="text-[9.5px] font-medium uppercase tracking-wider px-1 py-0.5 rounded bg-emerald-500/15 text-emerald-700">
                  live
                </span>
              )}
            </span>
            <span className="font-mono tabular-nums text-ink-muted shrink-0">
              {formatTimer(row.seconds)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface BreakdownRow {
  phase: FieldPhase;
  seconds: number;
  /** True for the row whose entry is currently open. */
  live: boolean;
}

/** Phase display order — matches the linear workflow + paused as a sibling. */
const DISPLAY_ORDER: FieldPhase[] = [
  'traveling',
  'on_site',
  'work_in_progress',
  'paused',
  'work_completed',
];

export function computeBreakdown(
  entries: ReadonlyArray<TimeEntry>,
  active: { openEntry: TimeEntry; currentPhase: FieldPhase } | null,
  openSec: number,
): BreakdownRow[] {
  const totals = new Map<FieldPhase, number>();
  const openEntryId = active?.openEntry.id ?? null;
  for (const entry of entries) {
    if (entry.id === openEntryId) {
      // Skip the open entry — we use the live `openSec` instead so the
      // row ticks every second instead of waiting for refetch.
      continue;
    }
    const prev = totals.get(entry.phase) ?? 0;
    totals.set(entry.phase, prev + (entry.durationSec ?? 0));
  }
  if (active) {
    const prev = totals.get(active.currentPhase) ?? 0;
    totals.set(active.currentPhase, prev + openSec);
  }

  return DISPLAY_ORDER.filter((phase) => (totals.get(phase) ?? 0) > 0).map(
    (phase) => ({
      phase,
      seconds: totals.get(phase) ?? 0,
      live: active?.currentPhase === phase,
    }),
  );
}

function phaseDotColor(phase: FieldPhase): string {
  switch (phase) {
    case 'traveling':
      return 'var(--color-text-info)';
    case 'on_site':
    case 'work_in_progress':
      return 'var(--trade-pm, #1D9E75)';
    case 'paused':
      return 'var(--trade-sales, #EF9F27)';
    case 'work_completed':
      return 'var(--trade-pm, #1D9E75)';
    default:
      return 'var(--color-text-subtle, #6B7280)';
  }
}
